import { useEffect, useState } from "react";
import PromotionStrip from "lib/components/Promotion/PromotionStrip";
import Link from "next/link";
import { useRouter } from "next/router";
import { useSelector } from "react-redux";
import useSWR from "swr";
import { useAppDispatch } from "redux/hooks";
import { getUser, isLoggedIn, logout } from "redux/reducers/userSlice";
import { getAllBoardList } from "lib/common/endpoints/public";
import { logOut } from "lib/common/helper/login";
import { Board } from "lib/common/types/board";
import { createClassroomSlug } from "utils/slugify";
import Avatar from "../atomic/Avatar";
import Button from "../atomic/Button";
import SignIn from "../SignIn";
import { BigLogo } from "lib/components/atomic/Vectors";
import { RiArrowDownSLine, RiMenuFill, RiCloseLine } from "react-icons/ri";
import { Menu, Dropdown, Drawer } from "antd";
import { Phone, SquaresFour, User, SignOut } from "@phosphor-icons/react";
import {
  hideAuthDrawer,
  isSignInOpen,
  showSignInDrawer,
  showSignUpDrawer,
} from "redux/reducers/drawerSlice";
import SignUpDrawer from "../Signup/SignUpDrawer";
import { message as _message } from "antd";
import { imgUrl, resolveImgUrl } from "utils/constants";

function Navbar() {
  const [visible, setVisible] = useState(false);
  const [isTop, setIsTop] = useState(true);
  const router = useRouter();
  const dispatch = useAppDispatch();
  const isLoggedIn_ = useSelector(isLoggedIn);
  const user = useSelector(getUser);
  const _isSignInOpen = useSelector(isSignInOpen);
  const { data: boards } = useSWR(getAllBoardList);

  useEffect(() => {
    // Listening on document.body (and reading its scrollTop) missed every
    // scroll on the homepage: body's actual scrollTop stayed 0 there even
    // while the page visibly scrolled, because the real scrolling box on
    // that page is the document/html element, not body (which one ends up
    // being the scrolling box depends on each page's own content/height
    // stacking). window's scroll event fires regardless of which element
    // is the real scrolling box, so it works on every page uniformly.
    const handleScroll = () => setIsTop(window.scrollY === 0);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const logOut_ = () => {
    logOut();
    dispatch(logout());
    _message?.success("Logged out successfully");
  };

  const boardsMenuItems = boards?.data
    ?.filter((b: Board) => b.isPublished)
    .map((board: Board) => ({
      key: board._id.$oid,
      label: (
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 flex items-center justify-center text-white font-bold text-xs">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={board.logo ? resolveImgUrl(board.logo) : "/img/class.png"}
              className="rounded-xl"
              alt={board.name}
            />
          </div>
          <Link
            href={`/goal/${createClassroomSlug(board.name, board._id.$oid)}`}
            className="px-4 text-black py-2 block hover:bg-zinc-100"
          >
            {board.name}
          </Link>
        </div>
      ),
    }));

  const phoneMenuItems = ["9851051663", "01-5918663", "01-5917663"].map(
    (num) => ({
      key: num,
      label: (
        <a href={`tel:${num}`} className="flex items-center gap-2 px-2 py-1">
          <Phone size={13} className="text-primary flex-shrink-0" />
          {num}
        </a>
      ),
    }),
  );

  const loggedinMenuItems = [
    {
      key: "dashboard",
      className: "lg:hidden",
      label: (
        <Link href="/dashboard">
          <div className="px-4 py-2 cursor-pointer flex items-center gap-2 text-gray-700 hover:text-primary transition-colors">
            <SquaresFour size={16} />
            <span>Dashboard</span>
          </div>
        </Link>
      ),
    },
    {
      key: "profile",
      label: (
        <Link href="/profile">
          <div className="px-4 py-2 cursor-pointer flex items-center gap-2 text-gray-700 hover:text-primary transition-colors">
            <User size={16} />
            <span>Profile</span>
          </div>
        </Link>
      ),
    },
    {
      key: "logout",
      label: (
        <div
          className="px-4 py-2 cursor-pointer flex items-center gap-2 text-error-600 hover:bg-error-50 transition-colors rounded-b-md"
          onClick={logOut_}
        >
          <SignOut size={16} />
          <span>Logout</span>
        </div>
      ),
    },
  ];

  return (
    <div
      className={`w-full z-50 transition-all duration-300 ${
        router.pathname === "/"
          ? `fixed top-0 ${!isTop ? "bg-white/70 backdrop-blur-md border-b border-gray-200/50 shadow-sm" : ""}`
          : "sticky top-0 bg-white/70 backdrop-blur-md border-b shadow-sm"
      }`}
    >
      <PromotionStrip />
      <nav
        className="container mx-auto px-4 flex justify-between items-center py-4"
        id="clamphook_navbar"
      >
        <div className="flex items-center gap-3">
          <Link
            href="/"
            title="Home"
            className="flex flex-col items-start leading-none"
          >
            <BigLogo className="text-primary fill-current" />
            <span className="text-[10px] font-medium text-primary mt-0.5 tracking-wide">
              IOE & CSIT Entrance Prep Platform
            </span>
          </Link>
          <div className="flex ml-6 max-lg:hidden items-center space-x-1">
            <Link
              href="/notice"
              className="px-3 py-1.5 rounded-lg text-sm font-semibold text-gray-700 hover:text-primary hover:bg-primary-50 transition-colors"
            >
              Notice
            </Link>
            <Link
              href="/syllabus"
              className="px-3 py-1.5 rounded-lg text-sm font-semibold text-gray-700 hover:text-primary hover:bg-primary-50 transition-colors"
            >
              Syllabus
            </Link>
            <Link
              href="/demo"
              className="px-3 py-1.5 rounded-lg text-sm font-semibold text-gray-700 hover:text-primary hover:bg-primary-50 transition-colors"
            >
              Demo
            </Link>
            <Link
              href="/blog"
              className="px-3 py-1.5 rounded-lg text-sm font-semibold text-gray-700 hover:text-primary hover:bg-primary-50 transition-colors"
            >
              Blog
            </Link>
            {boards && (
              <Dropdown menu={{ items: boardsMenuItems }} trigger={["click"]}>
                <span
                  className="ant-dropdown-link flex items-center px-3 py-1.5 rounded-lg text-sm font-semibold text-gray-700 hover:text-primary hover:bg-primary-50 transition-colors cursor-pointer"
                  onClick={(e) => e.preventDefault()}
                >
                  Courses
                  <RiArrowDownSLine className="ml-1 h-4 w-4 text-gray-400" />
                </span>
              </Dropdown>
            )}
            <Link
              href="/contact"
              className="px-3 py-1.5 rounded-lg text-sm font-semibold text-gray-700 hover:text-primary hover:bg-primary-50 transition-colors"
            >
              Contact
            </Link>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Dropdown
            menu={{ items: phoneMenuItems }}
            trigger={["click"]}
            placement="bottomRight"
          >
            <span className="hidden lg:flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold text-primary border border-primary/30 hover:bg-primary-50 transition-colors cursor-pointer select-none">
              <Phone size={14} />
              9851051663
              <RiArrowDownSLine className="h-4 w-4 text-primary/60" />
            </span>
          </Dropdown>

          {isLoggedIn_ ? (
            <div className="flex items-center gap-4">
              <Link
                href="/dashboard"
                className="hidden lg:flex items-center gap-2 px-4 py-2 bg-primary text-white text-sm font-semibold rounded-lg hover:bg-primary-600 transition-all shadow-sm transform hover:-translate-y-0.5 active:scale-95"
              >
                <SquaresFour size={18} weight="fill" />
                Dashboard
              </Link>
              <Dropdown
                menu={{ items: loggedinMenuItems }}
                trigger={["click"]}
                placement="bottomRight"
              >
                <Avatar
                  className="text-base font-sans bg-gray-200 cursor-pointer ring-2 ring-transparent hover:ring-primary/50 transition-all"
                  size={10}
                  label={user?.userData?.username}
                  image={
                    user?.userProfilePicture
                      ? resolveImgUrl(user?.userProfilePicture)
                      : user?.userAvatarPicture
                        ? `/avatar/${user?.userAvatarPicture}`
                        : "/img/logo/App_Icon_Primary.png"
                  }
                />
              </Dropdown>
            </div>
          ) : (
            <>
              <button
                className="hidden lg:block py-2 px-4 text-sm font-medium bg-primary text-white rounded-md hover:bg-primary-600 transition-colors"
                onClick={() => dispatch(showSignUpDrawer())}
              >
                Register
              </button>
              <Button
                className="hidden lg:block py-2 px-4 rounded-md text-sm font-medium hover:bg-primary hover:text-white transition-colors"
                onClick={() => dispatch(showSignInDrawer())}
                variant="PRIMARY"
              >
                Sign in
              </Button>
            </>
          )}

          {/* Mobile Menu Button */}
          <Button
            className="lg:hidden p-2"
            onClick={() => setVisible(true)}
            aria-label="Open menu"
          >
            <RiMenuFill className="w-6 h-6" />
          </Button>
        </div>
      </nav>

      {/* Sign In Drawer */}
      <Drawer
        placement="right"
        onClose={() => dispatch(hideAuthDrawer())}
        open={_isSignInOpen && !isLoggedIn_}
        closable
        width={"400px"}
      >
        <SignIn />
      </Drawer>

      {/* Sign Up Drawer */}
      <SignUpDrawer />

      {/* Mobile Navigation Drawer */}
      <Drawer
        placement="left"
        onClose={() => setVisible(false)}
        open={visible}
        closable={false}
      >
        <div className="relative w-full h-full p-6 space-y-4">
          {/* Close Button */}
          <button
            onClick={() => setVisible(false)}
            className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
            aria-label="Close menu"
          >
            <RiCloseLine className="w-5 h-5" />
          </button>

          <Link
            href="/notice"
            className="block text-gray-700 dark:text-gray-200 text-base font-medium hover:text-primary transition"
          >
            Notice
          </Link>
          <Link
            href="/syllabus"
            className="block text-gray-700 dark:text-gray-200 text-base font-medium hover:text-primary transition"
          >
            Syllabus
          </Link>
          <Link
            href="/demo"
            className="block text-gray-700 dark:text-gray-200 text-base font-medium hover:text-primary transition"
          >
            Demo
          </Link>
          <Link
            href="/blog"
            className="block text-gray-700 dark:text-gray-200 text-base font-medium hover:text-primary transition"
          >
            Blog
          </Link>
          <div>
            <p className="text-gray-700 dark:text-gray-200 text-base mb-2 font-medium">
              Courses
            </p>
            {boards?.data?.map(
              (board: Board) =>
                board.isPublished && (
                  <Link
                    href={`/goal/${createClassroomSlug(board.name, board._id.$oid)}`}
                    key={board._id.$oid}
                    className="block px-4 py-2 cursor-pointer hover:bg-zinc-100 rounded-md"
                  >
                    {board.name}
                  </Link>
                ),
            )}
          </div>
          <Link
            href="/contact"
            className="block text-gray-700 dark:text-gray-200 text-base font-medium hover:text-primary transition"
          >
            Contact
          </Link>

          {/* Mobile Action Buttons */}
          <div className="space-y-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            {isLoggedIn_ ? (
              <>
                <div className="flex items-center gap-3 mb-4">
                  <Avatar
                    className="text-base font-sans bg-gray-200 cursor-default shadow-sm"
                    size={10}
                    label={user?.userData?.username}
                    image={
                      user?.userProfilePicture
                        ? resolveImgUrl(user?.userProfilePicture)
                        : user?.userAvatarPicture
                          ? `/avatar/${user?.userAvatarPicture}`
                          : "/img/logo/App_Icon_Primary.png"
                    }
                  />
                  <div className="flex flex-col">
                    <span className="font-semibold text-gray-800 dark:text-gray-100 text-sm leading-tight">
                      {user?.fullName || "User Name"}
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      @{user?.userData?.username}
                    </span>
                  </div>
                </div>

                <Link
                  href="/dashboard"
                  onClick={() => setVisible(false)}
                  className="block w-full"
                >
                  <Button
                    className="py-2.5 px-4 w-full text-sm font-medium flex justify-center items-center gap-2 shadow-sm"
                    variant="PRIMARY"
                  >
                    <SquaresFour size={18} weight="fill" />
                    Dashboard
                  </Button>
                </Link>

                <Link
                  href="/profile"
                  onClick={() => setVisible(false)}
                  className="block w-full"
                >
                  <Button className="py-2.5 px-4 w-full text-sm font-medium flex justify-center items-center gap-2 bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200 shadow-sm transition-colors">
                    <User size={18} weight="fill" />
                    Profile
                  </Button>
                </Link>

                <Button
                  className="py-2.5 px-4 w-full text-sm font-medium flex justify-center items-center gap-2 bg-red-50 text-red-600 hover:bg-red-100 border border-red-100 shadow-sm transition-colors"
                  onClick={() => {
                    setVisible(false);
                    logOut_();
                  }}
                >
                  <SignOut size={18} weight="bold" />
                  Logout
                </Button>
              </>
            ) : (
              <>
                <Button
                  className="py-3 px-4 w-full text-base"
                  onClick={() => {
                    dispatch(showSignUpDrawer());
                    setVisible(false);
                  }}
                  variant="PRIMARY"
                >
                  Register
                </Button>
                <Button
                  className="py-3 px-4 w-full text-base"
                  onClick={() => {
                    dispatch(showSignInDrawer());
                    setVisible(false);
                  }}
                  variant="PRIMARY"
                >
                  Sign in
                </Button>
              </>
            )}
          </div>
        </div>
      </Drawer>
    </div>
  );
}

export default Navbar;
